import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Animated, Pressable, Text, TextInput, View } from "react-native";
import { usePostHog } from "posthog-js/react";
import { MAX_ANSWER_LENGTH, MAX_QUESTION_LENGTH } from "./faq-constants";
import { IFAQ } from "../../../../types/faq";
import { validateFaq } from "../../../../utils/setting-helpers";
import { useSettingsData } from "../../../../contexts/SettingsDataContext";
import { Check, Edit2, Trash2, X } from "lucide-react-native";

const MIN_QUESTION_HEIGHT = 24;
const MIN_ANSWER_HEIGHT = 45;

function FaqItem({
  isExpanded,
  setIsExpanded,
  faq,
  setFaq,
  onDelete,
  isNew = false,
  disabled = false,
}: {
  isExpanded: boolean;
  setIsExpanded: (value: boolean) => void;
  faq: IFAQ;
  setFaq: (faq: IFAQ) => void;
  onDelete: () => void;
  isNew?: boolean;
  disabled?: boolean;
}) {
  const posthog = usePostHog();

  const [isEditing, setIsEditing] = useState(isNew);
  const [animation] = useState(new Animated.Value(isExpanded ? 1 : 0));
  const [questionHeight, setQuestionHeight] = useState(MIN_QUESTION_HEIGHT);
  const [answerHeight, setAnswerHeight] = useState(MIN_ANSWER_HEIGHT);
  const [selection, setSelection] = useState<
    { start: number; end: number } | undefined
  >();

  const { shouldBlurFaqs, originalSettings } = useSettingsData();
  const questionInputRef = useRef<TextInput>(null);
  const answerInputRef = useRef<TextInput>(null);

  useEffect(() => {
    if (!isNew) {
      blurInputs();
    }
  }, []);

  useEffect(() => {
    if (shouldBlurFaqs) {
      blurInputs();
      setIsEditing(false);
    }
  }, [shouldBlurFaqs]);

  const blurInputs = () => {
    questionInputRef.current?.blur();
    answerInputRef.current?.blur();
  };

  useEffect(() => {
    if (isEditing && !isExpanded) {
      return;
    }
    Animated.timing(animation, {
      toValue: isExpanded ? 1 : 0,
      duration: 200,
      useNativeDriver: false,
    }).start();
  }, [isExpanded, animation, isEditing]);

  const handleQuestionContentSizeChange = useCallback((event: any) => {
    setQuestionHeight(event.nativeEvent.contentSize.height);
  }, []);

  const handleAnswerContentSizeChange = useCallback((event: any) => {
    setAnswerHeight(
      Math.max(MIN_ANSWER_HEIGHT, event.nativeEvent.contentSize.height)
    );
  }, []);

  useEffect(() => {
    if (disabled) {
      blurInputs();
      setIsEditing(false);
    }
  }, [disabled]);

  const isValidFaq = useMemo(() => validateFaq(faq), [faq]);

  const handleQuestionChange = (text: string) => {
    if (text.length > MAX_QUESTION_LENGTH) {
      return;
    }
    setFaq({ ...faq, question: text });
  };

  const handleAnswerChange = (text: string) => {
    if (text.length > MAX_ANSWER_LENGTH) {
      return;
    }
    setFaq({ ...faq, answer: text });
  };

  const onToggle = () => {
    if (isEditing) {
      return;
    }
    const newExpandedState = !isExpanded;
    posthog.capture("faq_toggled", {
      faq_id: faq.id,
      toggled_to: newExpandedState,
    });
    setIsExpanded(newExpandedState);
  };

  const handleEdit = () => {
    posthog.capture("faq_edit_started", {
      faq_id: faq.id,
    });
    setIsEditing(true);
    setIsExpanded(true);
    // focus on the question input
    setTimeout(() => {
      const input = questionInputRef.current;
      if (input) {
        input.focus();
        setSelection({
          start: faq.question.length,
          end: faq.question.length,
        });
      }
    }, 100);
  };

  const handleSave = async () => {
    if (!isValidFaq) {
      return;
    }
    posthog.capture("faq_edited", {
      faq_id: faq.id,
    });
    setIsEditing(false);
  };

  const getOriginalFaq = useCallback(() => {
    return originalSettings.faqs.find((f) => f.id === faq.id);
  }, [originalSettings.faqs, faq.id]);

  const handleDiscard = () => {
    if (isNew) {
      posthog.capture("faq_creation_cancelled", {
        faq_id: faq.id,
      });
      onDelete();
    } else {
      posthog.capture("faq_edit_cancelled", {
        faq_id: faq.id,
      });
      const originalFaq = getOriginalFaq();
      if (originalFaq) {
        setFaq(originalFaq);
      } else {
        onDelete();
      }
      setIsEditing(false);
    }
  };

  const handleDelete = () => {
    posthog.capture("faq_deleted", {
      faq_id: faq.id,
    });
    onDelete();
  };

  const isModified = useMemo(() => {
    if (isNew) return true;
    const originalFaq = getOriginalFaq();
    return (
      !originalFaq ||
      originalFaq.question !== faq.question ||
      originalFaq.answer !== faq.answer
    );
  }, [faq, getOriginalFaq, isNew]);

  return (
    <>
      <View className="flex flex-row justify-between items-start mb-3">
        <View
          className={`box-border rounded-lg bg-gray-50 overflow-hidden flex-1 ${
            isEditing
              ? "border border-gray-300"
              : "border border-gray-300 hover:bg-gray-100"
          }`}
        >
          {/* Header */}
          <Pressable
            className={`flex-row justify-between items-center ${
              isEditing ? "px-2 pt-2 pb-0" : "p-4"
            }`}
            onPress={onToggle}
            disabled={isEditing || disabled}
          >
            {isEditing ? (
              <TextInput
                ref={questionInputRef}
                className="flex-1 focus:border-gray-500 rounded-md focus:outline-none active:outline-none text-base font-semibold text-gray-900 border-gray-900 border px-2 py-1 overflow-hidden opacity-60 focus-within:opacity-100 focus-within:bg-white bg-gray-50"
                value={faq.question}
                onChangeText={handleQuestionChange}
                selection={selection}
                onSelectionChange={() => setSelection(undefined)}
                multiline
                autoFocus
                placeholder="Question"
                placeholderTextColor="gray"
                onContentSizeChange={handleQuestionContentSizeChange}
                maxLength={MAX_QUESTION_LENGTH}
                style={{
                  height: Math.max(MIN_QUESTION_HEIGHT, questionHeight),
                }}
                editable={!disabled}
              />
            ) : (
              <Text
                className="flex-1 text-base font-semibold text-gray-900 ml-[1px] mb-[1px]"
                onLayout={(event) => {
                  const height = event.nativeEvent.layout.height;
                  setQuestionHeight(Math.max(MIN_QUESTION_HEIGHT, height));
                }}
              >
                {faq.question}
              </Text>
            )}
            <View className="flex-row items-center space-x-2 ml-2">
              {isEditing ? (
                <View className="flex-row items-center py-2 px-2 pl-0 space-x-1">
                  <Pressable
                    onPress={handleDelete}
                    className="hover:bg-gray-200 rounded-md h-8 w-8 justify-center items-center"
                    disabled={disabled}
                  >
                    <Trash2 size={20} />
                  </Pressable>
                  <Pressable
                    onPress={handleSave}
                    className={`hover:bg-gray-200 rounded-md h-8 w-8 justify-center items-center ${
                      !isValidFaq || !isModified ? "opacity-50" : ""
                    } ${
                      !isValidFaq || !isModified ? "cursor-not-allowed" : ""
                    }`}
                    disabled={!isValidFaq || !isModified || disabled}
                  >
                    <Check size={24} color="#4CAF50" />
                  </Pressable>
                  <Pressable
                    onPress={handleDiscard}
                    className="hover:bg-gray-200 rounded-md h-8 w-8 justify-center items-center"
                    disabled={disabled}
                  >
                    <X size={24} color="#F43F5E" />
                  </Pressable>
                </View>
              ) : (
                <Pressable
                  onPress={handleEdit}
                  className="hover:bg-gray-200 rounded-md h-8 w-8 justify-center items-center"
                  disabled={disabled}
                >
                  <Edit2 size={18} />
                </Pressable>
              )}
            </View>
          </Pressable>
          {/* Content */}
          <Animated.View
            style={{
              height: animation.interpolate({
                inputRange: [0, 1],
                outputRange: [0, answerHeight || MIN_ANSWER_HEIGHT],
              }),
            }}
          >
            {isEditing ? (
              <TextInput
                ref={answerInputRef}
                value={faq.answer}
                onChangeText={handleAnswerChange}
                className="flex-1 text-base leading-relaxed rounded-md focus:outline-none active:outline-none border-gray-900 border px-2 py-1 overflow-hidden mx-2 opacity-60 focus-within:opacity-100 focus-within:bg-white bg-gray-50 mr-4 pt-[5px] mt-[3px] pl-[7px]"
                multiline
                placeholder="Answer"
                placeholderTextColor="gray"
                onContentSizeChange={handleAnswerContentSizeChange}
                maxLength={MAX_ANSWER_LENGTH}
                style={{
                  height: Math.max(MIN_ANSWER_HEIGHT, answerHeight),
                }}
                editable={!disabled}
              />
            ) : (
              <Text
                className="text-gray-800 text-base px-2 pb-4 leading-relaxed cursor-text mx-2 mt-[1px] mr-4"
                onLayout={(event) => {
                  const height = event.nativeEvent.layout.height;
                  setAnswerHeight(Math.max(MIN_ANSWER_HEIGHT, height));
                }}
              >
                {faq.answer}
              </Text>
            )}
          </Animated.View>
          {isEditing && <View className="h-2" />}
        </View>
      </View>
    </>
  );
}

export default FaqItem;
